import sizeof from "object-sizeof";
import ServiceUtils from "./service-utils";
import PublicTable from "./public-table";
import PublicForm from "./public-form";
import PublicApi from "./public-api";
import GdprPolicy from "./gdpr-policy";
import Format from "./format";
import Field from "./field";
import Feedback from "./feedback";
import EmailTemplate from "./email-template";
import EmailRenderUtils from "./email-render-utils";
import EmailEvent from "./email-event";
import DocumentValue from "./document-value";
import Collection from "./collection";
import Auth from "./auth";
import AppSettings from "./app-settings";
import Api from "./api";
import { clone, getEntryParams, isValidEmail, replaceAll, uniq } from "../utils";
import ISubstitution from "../interface/substitution";
import IServiceGroupKey from "../interface/service-group-key";
import IServiceEmail from "../interface/service-email";
import IRelationPathField from "../interface/relation-path-field";
import IPublicTable from "../interface/public-table";
import IPublicForm from "../interface/public-form";
import IField from "../interface/field";
import IEmailTemplate from "../interface/email-template";
import IEmailSendReponse from "../interface/email-send-response";
import IEmailPersonalization from "../interface/email-personalization";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import Global from "../global";
import ServiceUtil from "../enum/service-util";
import ServiceName from "../enum/service-name";
import ServiceApiName from "../enum/service-api-name";
import LogicalValue from "../enum/logical-value";
import GdprStatus from "../enum/gdpr-status";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import AppSetting from "../enum/app-setting";

class EmailSingleton {

    /* Public Methods - Common */

    public getEmissions(entityId: number): IRelationPathField[] {
        return Field.getRelationPathsFields(entityId, [FieldType.email], 2);
    }

    public getUrlSubstitutions(entityId: number): ISubstitution[] {
        return ServiceUtils.substitutions(entityId, [FieldType.url]);
    }

    public getNumberSubstitutions(entityId: number): ISubstitution[] {
        return ServiceUtils.substitutions(entityId, [FieldType.number]);
    }

    public getDateSubstitutions(entityId: number): ISubstitution[] {
        return ServiceUtils.substitutions(entityId, [FieldType.datetime], 2, false);
    }

    public getTestAddress(): string {
        return AppSettings.get(AppSetting.TestEmail, '') as string;
    }

    /* Public Methods - Email Sending */

    public async send(tpl: IEmailTemplate, docs: IDocument[], save: boolean = false, testAddress: string = ''): Promise<IEmailSendReponse> {
        tpl = await EmailTemplate.validateAndSave(tpl, save);
        if (tpl.body && docs.length) {
            let groupKey: IServiceGroupKey | null = null;
            testAddress = testAddress.trim();
            if (testAddress) {
                docs = [docs[0]];
                await AppSettings.set(AppSetting.TestEmail, testAddress);
            } else if (tpl.id) {
                groupKey = {
                    id: -1,
                    fCN: tpl.emissionPath,
                    split: [],
                    action: Feedback.getCurrentActionString(tpl.id)
                };
            }
            const body: string = await this.prepareBody(tpl.body);
            const data: IServiceEmail = {
                from: {
                    name: tpl.fromName,
                    email: tpl.fromEmail
                },
                content: [{
                    type: Global.EMAIL_CONTENT_TYPE,
                    value: body
                }],
                subject: tpl.subject,
                personalizations: await this.getPersonalizations(docs, tpl, body, !!testAddress)
            };
            if (testAddress) {
                data.personalizations.forEach((p: IEmailPersonalization) => {
                    p.to[0].email = testAddress;
                });
            }
            /* From Email should default if current server is not production */
            if (Auth.getDefaultInstance() !== Global.PROD_INSTANCE_NAME) {
                data.from.email = Global.SYS_EMAIL_ADDRESS;
            }
            const response: IEmailSendReponse = await this.validate(data, tpl);
            if (response.valid.length) {
                await this.executeByChunks(groupKey, data, response.valid);
            }
            await Collection.resetFeedback();
            return response;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Private Public Methods */

    public async sendSingle(body: string, subject: string, to: string[], cc: string[], fromName: string): Promise<void> {
        const pers: IEmailPersonalization[] = [];
        to.forEach((email: string) => {
            const personalization: IEmailPersonalization = {
                substitutions: {},
                to: [{ email }],
                cc: cc.map((c: string) => {
                    return { email: c };
                })
            };
            if (personalization.cc && !personalization.cc.length) {
                delete personalization.cc;
            }
            pers.push(personalization);
        });
        await Api.svcExec(ServiceName.Email, ServiceApiName.SendGridNoNotif, null, {
            content: [{
                type: Global.EMAIL_CONTENT_TYPE,
                value: body
            }],
            from: {
                email: Global.SYS_EMAIL_ADDRESS,
                name: fromName
            },
            personalizations: pers,
            subject: subject
        });
    }

    public async getSubstitutionValue(doc: IDocument, sub: ISubstitution, renderUrl: boolean = false): Promise<string> {
        const value: IDocumentValue = await DocumentValue.getByPath(doc, sub.pathId);
        const field: IField | null = Field.get(sub.pathId[sub.pathId.length - 1]);
        if (field) {
            let result: string = Format.byPath(value, sub.pathId, sub.option);
            if (field.uiType === FieldType.url && renderUrl) {
                result = `<a href="${result}">${result}</a>`;
            }
            return EmailRenderUtils.renderLinebreaks(result);
        }
        return '';
    }

    public async replaceSubstitutions(source: string, entityId: number, doc: IDocument, renderUrl: boolean = false): Promise<string> {
        let result = `${source}`;
        const subs: ISubstitution[] = ServiceUtils.getSourceSubstitutions(entityId, result);
        for (let i = 0; i < subs.length; i++) {
            const sub: ISubstitution = subs[i];
            const value: string = await Email.getSubstitutionValue(doc, sub, renderUrl);
            const original: string = `[${ServiceUtil.Sub}](${sub.pathId};;${sub.option})`;
            result = replaceAll(result, original, value);
        }
        return result;
    }

    /* Private Methods */

    private async executeByChunks(groupKey: IServiceGroupKey | null, data: IServiceEmail, personalizations: IEmailPersonalization[]) {
        /* SendGrid Restriction: "There is a limit of 1000 total recipients (to + cc + bcc) per mail/send request" */
        const chunks: IEmailPersonalization[][] = [[]];
        let currentIndx: number = 0;
        let currentSize: number = 0;
        if (groupKey) {
            groupKey.split = [];
        }
        personalizations.forEach((pers: IEmailPersonalization) => {
            const bcc: number = pers.bcc ? pers.bcc.length : 0;
            const cc: number = pers.cc ? pers.cc.length : 0;
            const size: number = pers.to.length + cc + bcc;
            const nextSize: number = currentSize + size;
            if (nextSize <= Global.EMAIL_CHUNK_RECIPIENTS_NUMBER) {
                currentSize = nextSize;
            } else {
                currentIndx++;
                chunks[currentIndx] = [];
                currentSize = size;
            }
            if (groupKey) {
                groupKey.split.push({
                    id: pers.docId as number,
                    key: pers.to[0].email
                });
            }
            delete pers.docId;
            chunks[currentIndx].push(pers);
        });
        try {
            for (let i = 0; i < chunks.length; i++) {
                data.personalizations = chunks[i];
                await Api.svcExec(ServiceName.Email, ServiceApiName.SendGrid, groupKey, data);
            }
        } catch (e) {
            throw ErrorCode.ApiInternal;
        }
    }

    private async validate(data: IServiceEmail, tpl: IEmailTemplate): Promise<IEmailSendReponse> {
        const result: IEmailSendReponse = { valid: [], invalid: [] };
        for (let i = 0; i < data.personalizations.length; i++) {
            const pers: IEmailPersonalization = data.personalizations[i];
            const to: string = pers.to.map(cc => cc.email)[0];
            const errorCode: ErrorCode | null = await this.getErrorCode(to, tpl, pers);
            if (errorCode) {
                result.invalid.push({
                    personalization: pers,
                    status: errorCode,
                    address: to
                });
            } else {
                result.valid.push(pers);
            }
        }
        return result;
    }

    private async getErrorCode(to: string, tpl: IEmailTemplate, pers: IEmailPersonalization): Promise<ErrorCode | null> {
        const bcc: string[] = pers.bcc ? pers.bcc.map(bcc => bcc.email) : [];
        const cc: string[] = pers.cc ? pers.cc.map(cc => cc.email) : [];
        const emails: string[] = [...bcc, ...cc, to];
        if (!to.length) {
            return ErrorCode.EmailInvalidRecipientEmpt;
        } else if (!isValidEmail(to)) {
            return ErrorCode.EmailInvalidRecipientAdrs;
        } else if (tpl.gdprOnly && !(await GdprPolicy.isAllowed(to))) {
            return ErrorCode.EmailInvalidRecipientGdpr;
        } else if (emails.length !== uniq(emails).length) {
            return ErrorCode.EmailInvalidRecipientUniq;
        } else if (this.getPersonalizationLength(pers) > Global.EMAIL_PERS_MAX_SIZE) {
            return ErrorCode.EmailPersonalDataOversize;
        }
        return null;
    }

    private getPersonalizationLength(pers: IEmailPersonalization): number {
        return sizeof(pers.to[0]) + sizeof(pers.substitutions);
    }

    private async prepareBody(original: string): Promise<string> {
        let result: string = this.prepareStyles(original);
        /* Encode substitutions to avoid spoiling substitutions in button URL */
        result = EmailRenderUtils.encodeSubstitutions(result);
        result = EmailRenderUtils.renderButtons(result);
        result = await EmailRenderUtils.replaceTagsToValue(result);
        result = await EmailRenderUtils.renderSignatures(result);
        /* SendGrid Restriction: Substitutions are limited to 10000 bytes per personalization block */
        /* To avoid this restriction we are prerendering some HTML to exclude it from personalization */
        /* Pre-render Public Form */
        const subsForm: string[] = ServiceUtils.getEntries(result, ServiceUtil.Form);
        subsForm.forEach((form: string) => {
            result = EmailRenderUtils.prerenderForm(result, form);
        });
        /* Pre-render Public Access */
        const subsAccs: string[] = ServiceUtils.getEntries(result, ServiceUtil.Access);
        subsAccs.forEach((acc: string) => {
            result = EmailRenderUtils.prerenderAccess(result, acc);
        });
        /* Pre-render Questions */
        const subsPoll: string[] = ServiceUtils.getEntries(result, ServiceUtil.Poll);
        subsPoll.forEach((poll: string) => {
            result = EmailRenderUtils.prerenderPoll(result, poll);
        });
        /* Pre-render Gdpr */
        const subsGdpr: string[] = ServiceUtils.getEntries(result, ServiceUtil.Gdpr);
        subsGdpr.forEach((gdpr: string) => {
            result = EmailRenderUtils.prerenderGdpr(result, gdpr);
        });
        /* Pre-render Events */
        const subsEvent: string[] = ServiceUtils.getEntries(result, ServiceUtil.Event);
        subsEvent.forEach((event: string) => {
            result = EmailRenderUtils.prerenderEvent(result, event);
        });
        /* Decode substitutions back, it was encoded at the beggining of function */
        result = EmailRenderUtils.decodeSubstitutions(result);
        return result;
    }

    private async getPersonalizations(original: IDocument[], tpl: IEmailTemplate, body: string, isTest: boolean): Promise<IEmailPersonalization[]> {
        /* Define Substitutions */
        const subsSubj: ISubstitution[] = ServiceUtils.getSourceSubstitutions(tpl.entityId, tpl.subject);
        const subsBody: ISubstitution[] = ServiceUtils.getSourceSubstitutions(tpl.entityId, body);
        const subsValues: ISubstitution[] = uniq([...subsSubj, ...subsBody]);
        const subsPoll: string[] = ServiceUtils.getEntries(body, ServiceUtil.Poll);
        const subsGdpr: string[] = ServiceUtils.getEntries(body, ServiceUtil.Gdpr);
        const subsForm: string[] = ServiceUtils.getEntries(body, ServiceUtil.Form);
        const subsAccs: string[] = ServiceUtils.getEntries(body, ServiceUtil.Access);
        /* Encode substitutions to avoid spoiling substitutions in button URL */
        body = EmailRenderUtils.encodeSubstitutions(body);
        const subsEvent: string[] = ServiceUtils.getEntries(body, ServiceUtil.Event);
        /* Decode substitutions back, it was encoded at the beggining of function */
        body = EmailRenderUtils.decodeSubstitutions(body);
        /* Define Personalizations */
        const personalizations: IEmailPersonalization[] = [];
        const docs: IDocument[] = clone(original);
        for (let i = 0; i < docs.length; i++) {
            const doc: IDocument = docs[i];
            const docSubs: { [key: string]: string } = {};
            const address: string = await DocumentValue.getByPath(doc, tpl.emissionPath) as string;
            /* Public Form URL Substitutions */
            for (let j = 0; j < subsForm.length; j++) {
                const form: string = subsForm[j];
                const prm: (string | number)[] = getEntryParams(form);
                let formId: number = prm[0] as number;
                if (prm[1] && !isNaN(Number(prm[1]))) {
                    // TODO - Migrate existing templates when everything will be fine
                    const form: IPublicForm | null = await PublicForm.getByMapping(`${prm[1]}-${prm[0]}`);
                    formId = form ? form.id as number : 0;
                }
                docSubs[form] = PublicApi.getFormAddress(formId, doc.id as number);
            }
            /* Public Table URL Substitutions */
            for (let j = 0; j < subsAccs.length; j++) {
                const acc: string = subsAccs[j];
                const prm: (string | number)[] = getEntryParams(acc);
                let tableId: number = prm[0] as number;
                if (prm[1] && !isNaN(Number(prm[1]))) {
                    // TODO - Migrate existing templates when everything will be fine
                    const table: IPublicTable | null = await PublicTable.getByMapping(`${prm[1]}-${prm[0]}`);
                    tableId = table ? table.id as number : 0;
                }
                docSubs[acc] = PublicApi.getTableAddress(tableId, tpl.entityId, doc);
            }
            /* Question Substitutions */
            subsPoll.forEach((poll: string) => {
                const prm: (string | number)[] = getEntryParams(poll);
                docSubs[poll] = PublicApi.getQuestionAddress(doc.id as number, prm[0] as number, `${prm[1]}` as LogicalValue);
            });
            /* Gdpr Substitutions */
            subsGdpr.forEach((gdpr: string) => {
                const prm: (string | number)[] = getEntryParams(gdpr);
                docSubs[gdpr] = PublicApi.getGdprAddress(address, `${prm[0]}` as GdprStatus);
            });
            /* Event Substitutions */
            for (let j = 0; j < subsEvent.length; j++) {
                const event: string = subsEvent[j];
                docSubs[EmailRenderUtils.decodeSubstitutions(event)] = await EmailEvent.getEventUrl(event, tpl.entityId, doc);
            }
            /* Values Substitutions */
            for (let j = 0; j < subsValues.length; j++) {
                const sub: ISubstitution = subsValues[j];
                const subValue: string = await this.getSubstitutionValue(doc, subsValues[j]);
                docSubs[`[${ServiceUtil.Sub}](${sub.pathId};;${sub.option})`] = subValue;
            }
            const personalization: IEmailPersonalization = {
                docId: doc.id as number,
                substitutions: docSubs,
                bcc: isTest ? [] : tpl.bcc.map((address: string) => { return { email: address.trim() } }),
                cc: isTest ? [] : tpl.cc.map((address: string) => { return { email: address.trim() } }),
                to: [{ email: address.trim() }]
            };
            /* SendGrid Restriction: "The cc array, when used, must at least have an email parameter..." */
            if (personalization.cc && !personalization.cc.length) {
                delete personalization.cc;
            }
            /* SendGrid Restriction: "The bcc array, when used, must at least have an email parameter..." */
            if (personalization.bcc && !personalization.bcc.length) {
                delete personalization.bcc;
            }
            personalizations.push(personalization);
        }
        return personalizations;
    }

    private prepareStyles(body: string): string {
        const lines: string[] = body.split('\n');
        for (let i = 0; i < lines.length; i++) {
            if (lines[i] === '<p></p>') {
                /* Replace composer empty lines with HTML linebreaks */
                lines[i] = '<br/>';
            } else if (lines[i].startsWith('<p>')) {
                const el: Element = this.stringToDom(lines[i]);
                /* Set styles for lines */
                el.setAttribute('style', 'margin:0;');
                const links: NodeListOf<HTMLAnchorElement> = el.querySelectorAll('a');
                links.forEach((elA: Element) => {
                    /* Set styles for links */
                    elA.setAttribute('style', 'color:#2996da;text-decoration:none;');
                    /* Check href for links */
                    const href: string | null = elA.getAttribute('href');
                    if (href && !href.startsWith('http')) {
                        elA.setAttribute('href', `http://${href}`);
                    }
                });
                lines[i] = this.domToString(el);
            } else if (lines[i].startsWith('<emailcode>')) {
                /* Remove <emailcode> tags, it is only needed for proper DraftJS displaying */
                lines[i] = lines[i].replace('<emailcode>', '');
                lines[i] = lines[i].replace('</emailcode>', '');
            } else if (lines[i].startsWith('<ul>')) {
                /* Set styles for lists */
                lines[i] = '<ul style="margin:16px 0;padding:0;list-style-position:inside;">';
            } else if (lines[i].startsWith('<ol>')) {
                /* Set styles for lists */
                lines[i] = '<ol style="margin:16px 0;padding:0;list-style-position:inside;">';
            } else if (lines[i].startsWith('<li')) {
                /* Set styles for list items */
                const el: Element = this.stringToDom(lines[i]);
                el.removeAttribute('dir');
                el.setAttribute('style', 'margin:0;');
                lines[i] = this.domToString(el);
            }
        }
        return `<div style="font-family:Helvetica,Arial,sans-serif;font-size:14px;">${lines.join('')}</div>`;
    }

    private stringToDom(html: string): Element {
        const placeholder: HTMLDivElement = document.createElement('div');
        placeholder.innerHTML = html;
        return placeholder.firstElementChild as Element;
    }

    private domToString(el: Element): string {
        const placeholder: HTMLDivElement = document.createElement('div');
        placeholder.appendChild(el);
        return placeholder.innerHTML;
    }

}

const Email = new EmailSingleton();

export default Email;